import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  InlineLoading,
  Link,
  Loading,
  InlineNotification
} from 'carbon-components-react';
import { Checkmark16 } from '@carbon/icons-react';
import { currentUser } from 'redux/session/selectors';
import { selectTeamsList, selectLoadingTeamsList } from 'redux/team/selectors';
import { useComponentMount, useEmptyCallback } from 'utilities/hooks.util';
import { TeamQueryParams, TEAM_NAME } from 'types';
import { getTeamsListStart } from 'redux/team/actions';

import { useSaga } from 'use-reducer-saga';
import {
  getEnquiryDetailsStart,
  moveEnquiryTeamStart
} from './localStore/actions';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';

const MoveEnquiryToTeam: React.FC<Props> = ({
  enquiryId,
  enquiryUserId,
  enquiryTeamId,
  onClose,
  refreshMap,
  setNeedRefresh,
  onRefreshTeamView,
  onGoToEnquiry,
  jobId,
  isAutoRenew,
  enquiryAutoRenewSeries
}) => {
  const user = useSelector(currentUser);
  const teams = useSelector(selectTeamsList);
  const loadingTeams = useSelector(selectLoadingTeamsList);
  const dispatch: any = useDispatch();
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { moveEnquiryTeam, isMovingEnquiryTeam, enquiryDetails } = localStore;
  const isARPartOfSeries: boolean = isAutoRenew
    ? enquiryAutoRenewSeries.length > 1
    : false;
  const [localMoving, setLocalMoving] = useState<boolean>(false);
  const [hidePrivate, setHidePrivate] = useState<boolean>(false);
  const [selected, setSelected] = useState<number>(
    enquiryDetails?.project_id || 0
  );
  const [selectedTeamName, setSelectedTeamName] = useState<string>('');
  const [disableMoveButton, setDisableMoveButton] = useState<boolean>(true);
  const [moveSuccessful, setMoveSuccessful] = useState<boolean>(false);
  const [enquiriesToMove, setEnquiriesToMove] = useState<Array<any>>([]);

  const onSelectTeam = (teamId: number, teamName: string) => {
    setSelected(teamId);
    setSelectedTeamName(teamName);
    if (enquiryDetails?.project_id === null && teamId === 0) {
      setDisableMoveButton(true);
    } else if (enquiryDetails?.project_id !== teamId) {
      setDisableMoveButton(false);
    } else {
      setDisableMoveButton(true);
    }
  };

  const handleMoveClick = (moveAll: boolean = false) => {
    setLocalMoving(true);
    const data: Array<any> = [];
    const enquiryIdArray: Array<any> = [];

    if (isARPartOfSeries && enquiryAutoRenewSeries.length > 1 && moveAll) {
      enquiryAutoRenewSeries.forEach((enq) => {
        const params = {
          enquiryId: enq.id,
          jobId: enq.jobId,
          currentTeamId: enquiryDetails?.project_id,
          newTeamId: selected
        };
        data.push(params);
        enquiryIdArray.push(enq.id);
      });
    } else {
      const params = {
        enquiryId,
        jobId,
        currentTeamId: enquiryDetails?.project_id,
        newTeamId: selected
      };
      data.push(params);
    }

    const params = {
      enquiryIds: enquiryIdArray,
      enquiryId,
      jobId,
      currentTeamId: enquiryDetails?.project_id,
      newTeamId: selected
    };

    setEnquiriesToMove(data);
    localDispatch(moveEnquiryTeamStart(params));
  };

  useEffect(() => {
    if (enquiryDetails) {
      setSelected(enquiryDetails.project_id || 0);
    }
  }, [enquiryDetails]);

  const renderMapAgain = useEmptyCallback(
    () => typeof refreshMap === 'function' && refreshMap()
  );

  useEffect(() => {
    if (moveEnquiryTeam && !isMovingEnquiryTeam) {
      setNeedRefresh(true);
      renderMapAgain();
      setTimeout(() => {
        setLocalMoving(false);
        setDisableMoveButton(true);
        setMoveSuccessful(true);
      }, 1500);
    }
  }, [moveEnquiryTeam, isMovingEnquiryTeam, setNeedRefresh, renderMapAgain]);

  useEffect(() => {
    // hide private team option when
    // 1) is external user that not turn from trial
    // 2) is external user which turn from trial in create enquiry page.
    // 3) If the enquiry is not own by the user and the enquiry already in a team
    if (user) {
      if (user.is_as_external_user && !user.is_trial) {
        setHidePrivate(!user.trial_organisation_name?.trim());
      } else if (enquiryTeamId && user.id !== enquiryUserId) {
        setHidePrivate(true);
      } else {
        setHidePrivate(false);
      }
    }
  }, [user, enquiryUserId, enquiryTeamId]);

  useComponentMount(() => {
    const params: TeamQueryParams = {
      sorting: 'name,asc'
    };
    localDispatch(getEnquiryDetailsStart({ enquiryId }));
    !teams && dispatch(getTeamsListStart(params));
  });

  return (
    <div
      className={`move-enquiry-to-team ${
        isARPartOfSeries ? 'move-enquiry-to-team-with-notification' : ''
      }`}
    >
      {isARPartOfSeries && !moveSuccessful && (
        <div>
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton={true}
            title={'This enquiry is part of a series'}
          />
        </div>
      )}
      <div
        className={`move-enquiry-to-team-wrapper ${
          moveSuccessful ? 'moved' : ''
        }`}
      >
        {!moveSuccessful ? (
          <div className="move-enquiry-to-team-selection">
            <div className="move-enquiry-to-team-title">
              Move enquiry to team
            </div>
            <div
              className={`move-enquiry-to-team-teams-list ${
                isARPartOfSeries
                  ? 'move-enquiry-to-team-teams-list-with-notification'
                  : ''
              }`}
            >
              {!loadingTeams && (
                <>
                  {!hidePrivate && (
                    <div className="list-container">
                      <div className="checkmark">
                        {selected === 0 && <Checkmark16 />}
                      </div>
                      <div
                        className={`list ${selected === 0 ? 'selected' : ''} `}
                        aria-label="Private team"
                        onClick={() => onSelectTeam(0, 'Private team')}
                      >
                        {TEAM_NAME.PRIVATE}
                      </div>
                    </div>
                  )}
                  {teams &&
                    teams.map((team: any, inx: number) => (
                      <div className="list-container" key={`team-${inx}`}>
                        <div className="checkmark">
                          {selected === team.id && <Checkmark16 />}
                        </div>
                        <div
                          className={`list ${
                            selected === team.id ? 'selected' : ''
                          } `}
                          aria-label={team.name}
                          onClick={() => onSelectTeam(team.id, team.name)}
                        >
                          {team.name}
                        </div>
                      </div>
                    ))}
                </>
              )}
              {loadingTeams && (
                <div className="loading-team-list-container">
                  <Loading withOverlay={false} />
                </div>
              )}
            </div>
            <div
              className={`move-enquiry-to-team-button-container ${
                isARPartOfSeries
                  ? 'move-enquiry-to-team-button-container-with-notification'
                  : ''
              }`}
            >
              <div className="only-buttons">
                <Button kind="tertiary" size="field" onClick={() => onClose()}>
                  Back
                </Button>

                <Button
                  kind="secondary"
                  size="field"
                  disabled={
                    disableMoveButton || isMovingEnquiryTeam || localMoving
                  }
                  onClick={() =>
                    handleMoveClick(isARPartOfSeries ? true : false)
                  }
                >
                  {!isMovingEnquiryTeam && !localMoving ? (
                    isARPartOfSeries ? (
                      `Move series (${enquiryAutoRenewSeries.length})`
                    ) : (
                      'Move'
                    )
                  ) : isARPartOfSeries && enquiriesToMove.length > 1 ? (
                    <InlineLoading
                      description={
                        !isMovingEnquiryTeam
                          ? `Moved series (${enquiryAutoRenewSeries.length})`
                          : isARPartOfSeries
                          ? `Move series (${enquiryAutoRenewSeries.length})`
                          : 'Move'
                      }
                      status={!isMovingEnquiryTeam ? 'finished' : 'active'}
                      aria-live={'assertive'}
                    />
                  ) : !isARPartOfSeries ? (
                    <InlineLoading
                      description={
                        !isMovingEnquiryTeam
                          ? 'Moved'
                          : isARPartOfSeries
                          ? `Move series (${enquiryAutoRenewSeries.length})`
                          : 'Move'
                      }
                      status={!isMovingEnquiryTeam ? 'finished' : 'active'}
                      aria-live={'assertive'}
                    />
                  ) : isARPartOfSeries ? (
                    `Move series (${enquiryAutoRenewSeries.length})`
                  ) : (
                    'Move'
                  )}
                </Button>
              </div>
              {isARPartOfSeries && (
                <div className="move-link-container">
                  <div>
                    <Link
                      onClick={() => handleMoveClick()}
                      disabled={
                        disableMoveButton || isMovingEnquiryTeam || localMoving
                      }
                    >
                      Move only this enquiry
                    </Link>
                  </div>
                  {(isMovingEnquiryTeam || localMoving) &&
                    enquiriesToMove.length <= 1 && (
                      <InlineLoading
                        status={!isMovingEnquiryTeam ? 'finished' : 'active'}
                        aria-live={'assertive'}
                      />
                    )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="move-enquiry-to-team-success">
            <div className="move-enquiry-to-team-title">
              {enquiriesToMove.length > 1
                ? `${enquiryAutoRenewSeries.length} enquiries moved`
                : 'Enquiry moved'}
            </div>
            <div className="move-enquiry-to-team-teams-list">
              <div className="list-container">
                <div className="checkmark">
                  <Checkmark16 />
                </div>
                <div>{selectedTeamName}</div>
              </div>
            </div>
            <div className="move-enquiry-to-team-button-container">
              <Button
                kind="tertiary"
                size="field"
                onClick={() =>
                  onGoToEnquiry(
                    selected,
                    enquiriesToMove.slice(-1)[0].enquiryId,
                    enquiriesToMove.slice(-1)[0].jobId
                  )
                }
              >
                {enquiriesToMove.length > 1
                  ? 'Go to latest enquiry'
                  : 'Go to enquiry'}
              </Button>
              <div className="refresh-team-link-container">
                <Link onClick={() => onRefreshTeamView()}>
                  Refresh current team view
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type Props = {
  refreshMap?: Function;
  onClose: Function;
  enquiryTeamId: number;
  enquiryId: number;
  enquiryUserId: number;
  closeDetailsPanel: Function;
  setNeedRefresh: Function;
  onRefreshTeamView: Function;
  onGoToEnquiry: Function;
  jobId: number | string;
  isAutoRenew: boolean;
  enquiryAutoRenewSeries: any[];
};

export default React.memo(MoveEnquiryToTeam);
